import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A sheet that slides up from the bottom of a phone's screen over a dimmed background (the position and order details with
 * their actions): a native modal dialog, so focus stays inside, Escape or the back gesture closes it, and so does a tap
 * on the dimmed part above it.
 */
export function BottomSheet({
  open,
  label,
  onClose,
  children,
}: {
  open: boolean;
  /** What the sheet is, for assistive technology. */
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label={label}
      onClose={onClose}
      onCancel={onClose}
      // A tap on the backdrop lands on the dialog element itself; the content below fills the dialog, so a tap on it never does.
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className="fixed inset-x-0 bottom-0 top-auto m-0 max-h-[85dvh] w-screen max-w-none overflow-auto rounded-t-xl bg-panel p-0 text-fg backdrop:bg-black/40"
    >
      {open && <div className="sheet-up pb-[env(safe-area-inset-bottom)]">{children}</div>}
    </dialog>
  );
}
